
export function AboutSection() {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const usps = [
    {
      icon: 'ri-user-settings-line',
      title: 'SME-Focused Design',
      description: 'Tailored for non-technical users and small business budgets'
    },
    {
      icon: 'ri-map-2-line',
      title: 'AI with Local Relevance',
      description: 'Trained on UK-centric threat data to reflect real regional attack patterns'
    },
    {
      icon: 'ri-graduation-cap-line',
      title: 'Proactive Education',
      description: 'In-built training nudges reduce future risk—turning users into the first line of defense'
    },
    {
      icon: 'ri-dashboard-3-line',
      title: 'Unified Platform',
      description: 'Combines phishing/smishing detection, employee awareness, and IT support into one simple tool'
    },
    {
      icon: 'ri-money-pound-circle-line',
      title: 'Affordable & Scalable',
      description: 'Subscription-based model accessible to businesses of all sizes'
    }
  ];

  return (
    <section id="about" className="py-24 bg-gradient-to-b from-gray-900 to-black">
      <div className="container mx-auto px-6">
        {/* Mission Statement */}
        <div className="text-center max-w-5xl mx-auto mb-20">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-8 leading-tight">
            Our <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Mission</span>
          </h2>
          <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-8">
            <p className="text-xl text-gray-300 leading-relaxed mb-6">
              To democratize cybersecurity by giving small businesses the same level of protection and 
              insight enjoyed by large enterprises without the complexity or high cost.
            </p>
            <p className="text-lg text-gray-400 leading-relaxed">
              Driscop believes that AI should protect people, not overwhelm them. Our approach simplifies 
              cyber defense into an automated, proactive, and educational experience that empowers SMEs 
              to operate confidently in a digital world.
            </p>
          </div>
        </div>

        {/* Problem & Solution */}
        <div className="grid md:grid-cols-2 gap-12 mb-20">
          {/* The Problem */}
          <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-red-300 mb-6 flex items-center gap-3">
              <i className="ri-error-warning-line text-3xl"></i>
              The Problem
            </h3>
            <div className="space-y-4 text-gray-300">
              <div className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-red-400 mt-1"></i>
                <p>"We can't afford enterprise security solutions"</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-red-400 mt-1"></i>
                <p>"Our employees are our weakest security link"</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-red-400 mt-1"></i>
                <p>"We don't have an IT team to manage complex tools"</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-red-400 mt-1"></i>
                <p>"Technical jargon overwhelms our business owners"</p>
              </div>
            </div>
          </div>

          {/* The Solution */}
          <div className="bg-green-500/10 border border-green-500/30 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-green-300 mb-6 flex items-center gap-3">
              <i className="ri-lightbulb-line text-3xl"></i>
              Our Solution
            </h3>
            <div className="space-y-4 text-gray-300">
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-green-400 mt-1"></i>
                <p>Affordable, subscription-based cybersecurity for any budget</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-green-400 mt-1"></i>
                <p>Turn staff from risk into your first line of defense</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-green-400 mt-1"></i>
                <p>Remote deployment with zero IT expertise required</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-green-400 mt-1"></i>
                <p>Simple dashboard that anyone can understand</p>
              </div>
            </div>
          </div>
        </div>

        {/* Unique Selling Points */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold text-white text-center mb-12">
            Why Choose <span className="text-blue-400">Driscop?</span>
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {usps.map((usp, index) => (
              <div 
                key={index}
                className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-6 hover:border-blue-500/50 transition-all duration-300 hover:bg-gray-900/70"
              >
                <div className="w-12 h-12 bg-blue-500/200/20 rounded-xl flex items-center justify-center mb-4">
                  <i className={`${usp.icon} text-2xl text-blue-400`}></i>
                </div>
                <h4 className="text-xl font-semibold text-white mb-3">{usp.title}</h4>
                <p className="text-gray-400 leading-relaxed">{usp.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Vision */}
        <div className="text-center">
          <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/20 rounded-2xl p-8 max-w-4xl mx-auto">
            <h3 className="text-3xl font-bold text-white mb-6">Our Vision</h3>
            <p className="text-xl text-gray-300 leading-relaxed mb-6">
              To become the go-to cybersecurity companion for UK SMEs and later expand across Europe, 
              enabling millions of small businesses to operate safely online.
            </p>
            <p className="text-lg text-gray-400 leading-relaxed">
              Long term, Driscop aims to integrate its AI engine into broader business protection services, 
              including fraud prevention, digital compliance, and secure communication platforms.
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <button 
            onClick={() => scrollToSection('solutions')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-xl text-lg font-semibold transition-all duration-300 hover:scale-105 hover:shadow-xl whitespace-nowrap cursor-pointer"
          >
            Discover Our Solutions
          </button>
        </div>
      </div>
    </section>
  );
}
